import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';
import { getPage, hasError } from '../../../states/page/selectors';
import withCalendar from '../../../containers/withCalendar';
import { submitChapsConfirmed, submitChapsReview, addMoreChapsPayment } from '../../../states/chaps/actions';
import ChapsLandingPage from '../ChapsLandingPage/ChapsLandingPage';
import ChapsPaymentList from '../ChapsPayments/ChapsPaymentList';
import ChapsReviewPage from '../ChapsReviewPage/ChapsReviewPage';
import ChapsSubmittedPage from '../ChapsSubmittedPage/ChapsSubmittedPage';
import AddPayeeLayoutContainer from '../../../payees/components/LayoutContainer';
import PaymentTimedOutModal from '../../../sharedComponents/PaymentTimedOutModal';
import { fetchCalendar } from '../../../states/calendar/actions';
import { isBatch, isBatchTRAObligate, isBatchTypeUpload } from '../../../states/batch/selectors';
import { fetchStatusCodeMapping } from '../../../states/statusMapping/actions';
import { goToPage, pages, resetApp, goToPreviousPage } from '../../../states/page/actions';
import { toggleBatch } from '../../../states/batch/actions';
import { isBatchEligible, getChapsPaymentsList } from '../../../states/chaps/selectors';
import { TYPE_CHAPS, calendarTypes } from '../../../config';
import ChapsPayeePage from '../ChapsPayeePage/ChapsPayeePage';
import AccountPickerPage from '../../../sharedComponents/AccountPickerPage/AccountPickerPage';
import { setSelectedFromAccount, setSelectedPaymentId } from '../../../states/payeeSelector/actions';
import { connectWrapper as ImportedChapsPaymentsPage } from '../../../importedPayments/components/fps/ImportedPaymentsPage';
import { getImportedPaymentList } from '../../../states/importedPaymentList/selector';
import EffectiveWarningSMEContainer from '../../../effectiveWarning/EffectiveWarningSMEContainer';
import { PaymentType } from '../../../effectiveWarning/utils/constants';
import { resetEffectiveWarning } from '../../../effectiveWarning/state/actions';
import { hasGroupApprovalsEnabled } from '../../../states/modes/selectors';

const mapState = (state) => ({
	page: getPage(state),
	hasError: hasError(state),
	hasGroupApprovalsEnabled: hasGroupApprovalsEnabled(state),
	isBatch: isBatch(state),
	isBatchEligible: isBatchEligible(state),
	isCHAPSTRAObligate: isBatchTRAObligate(state),
	payments: getChapsPaymentsList(state),
	importedPaymentList: getImportedPaymentList(state),
	isBatchTypeUpload: isBatchTypeUpload(state),
});

const mapDispatch = (dispatch) => ({
	submitChapsPaymentsForReview: () => dispatch(submitChapsReview()),
	submitChapsConfirmedPayments: () => dispatch(submitChapsConfirmed()),
	fetchCalendar: (calendarType) => dispatch(fetchCalendar(calendarType)),
	continueToPayments: () => dispatch(goToPage(pages.ChapsPayments)),
	handleToggleIsBatch: (isBatch) => dispatch(toggleBatch(isBatch)),
	fetchStatusCodeMapping: () => dispatch(fetchStatusCodeMapping()),
	setSelectedFromAccount: (fromAccount) => dispatch(setSelectedFromAccount(fromAccount)),
	selectPayment: (paymentId) => dispatch(setSelectedPaymentId(paymentId)),
	addChapsPayment: ({ fromAccount }) => dispatch(addMoreChapsPayment({ fromAccount })),
	reset: () => dispatch(resetApp),
	handleBack: () => dispatch(goToPage(pages.ChapsLandingPage)),
	resetEffectiveWarning: () => dispatch(resetEffectiveWarning()),
	goToPreviousPage: () => dispatch(goToPreviousPage()),
});

class LayoutContainer extends Component {
	constructor(props) {
		super(props);

		this.handleContinue = this.handleContinue.bind(this);
		this.handleReset = this.handleReset.bind(this);
	}

	componentDidMount() {
		this.props.fetchCalendar(calendarTypes.CALENDAR_TYPE_CHAPS);
		this.props.fetchStatusCodeMapping();
	}

	componentDidUpdate() {
		const { hasError, handleRoute, isBatchEligible, isBatch, handleToggleIsBatch, isBatchTypeUpload } = this.props;
		if (hasError) {
			handleRoute('WEB-ERROR');
		} else if (!isBatchEligible && isBatch && !isBatchTypeUpload) {
			handleToggleIsBatch(false);
		}
	}

	handleContinue() {
		const { page, submitChapsPaymentsForReview, submitChapsConfirmedPayments } = this.props;
		switch (page) {
			case pages.ImportedChapsPaymentsPage:
			case 'ChapsPayments':
				submitChapsPaymentsForReview();
				break;
			case 'ChapsReview':
			case pages.EffectiveWarning.warning:
			case pages.EffectiveWarning.paymentReason:
				submitChapsConfirmedPayments();
				break;
			default:
				break;
		}
	}

	handleReset() {
		const { resetPaymentList, reset, fetchCalendar, resetEffectiveWarning } = this.props;
		resetPaymentList();
		resetEffectiveWarning();
		reset();
		fetchCalendar(calendarTypes.CALENDAR_TYPE_CHAPS);
	}

	render() {
		const {
			page,
			handleSelectPaymentType,
			handleRoute,
			isBootStrapInProgress,
			handlePaymentInReview,
			handleComplete,
			handleBackParent,
			handleDisplayFullwidth,
			hasGroupApprovalsEnabled,
			isCHAPSTRAObligate,
			handleBack,
			firstSubmissionDate,
			importedPaymentList,
			goToPreviousPage,
			content,
			copCheckTriggered,
			sessionCOPChecks,
		} = this.props;

		switch (page) {
			case pages.ChapsLandingPage:
				return (
					<div data-cy="chaps-landing-page">
						<ChapsLandingPage
							handleDisplayFullwidth={handleDisplayFullwidth}
							onPayeeListContinue={this.handlePayeesSelected}
							showTemplateSelector
							paymentType={TYPE_CHAPS}
							handleSelectPaymentType={handleSelectPaymentType}
							isBootStrapInProgress={isBootStrapInProgress}
							firstSubmissionDate={firstSubmissionDate}
						/>
					</div>
				);
			case pages.ChapsPayeeSelector:
				return (
					<ChapsPayeePage
						copCheckTriggered={copCheckTriggered}
						firstSubmissionDate={firstSubmissionDate}
						handleDisplayFullwidth={handleDisplayFullwidth}
						sessionCOPChecks={sessionCOPChecks}
					/>
				);
			case pages.ChapsPayments:
				return (
					<div data-cy="chaps-payment-list-page" className="container container--lg chaps">
						<ChapsPaymentList
							copCheckTriggered={copCheckTriggered}
							handleContinue={this.handleContinue}
							handleBackParent={handleBackParent}
							handleDisplayFullwidth={handleDisplayFullwidth}
							sessionCOPChecks={sessionCOPChecks}
						/>
					</div>
				);
			case pages.ChapsReview: {
				const contentKey = isCHAPSTRAObligate ? 'traObligateContent' : 'content';
				return (
					<div data-cy="chaps-review-page" className="container container--lg chaps">
						<PaymentTimedOutModal handleRoute={handleRoute} />
						<ChapsReviewPage
							handleContinue={this.handleContinue}
							handlePaymentInReview={handlePaymentInReview}
							handleRoute={handleRoute}
							contentKey={contentKey}
							hasGroupApprovalsEnabled={hasGroupApprovalsEnabled}
						/>
					</div>
				);
			}
			case pages.ChapsSubmitted:
				return (
					<div data-cy="chaps-submitted-page" className="container container--lg chaps">
						<ChapsSubmittedPage
							handleRoute={handleRoute}
							handleReset={this.handleReset}
							handleComplete={handleComplete}
						/>
					</div>
				);
			case 'Payee':
				return (
					<div data-cy="chaps-payee-page" className="container container--lg chaps">
						<AddPayeeLayoutContainer
							copCheckTriggered={copCheckTriggered}
							handleRoute={handleRoute}
							sessionCOPChecks={sessionCOPChecks}
						/>
					</div>
				);
			case pages.ChapsAccountPicker:
				return <AccountPickerPage pageId={pages.ChapsLandingPage} />;

			case pages.ImportedChapsPaymentsPage:
				return (
					<div className="container container--lg">
						<ImportedChapsPaymentsPage
							handleContinue={this.handleContinue}
							handleDisplayFullwidth={handleDisplayFullwidth}
							handlePaymentInReview={handlePaymentInReview}
							handleBack={handleBack}
							importedPaymentList={importedPaymentList}
							type={TYPE_CHAPS}
						/>
					</div>
				);

			case pages.EffectiveWarning.warning:
			case pages.EffectiveWarning.paymentReason:
			case pages.EffectiveWarning.paymentCancelled:
				return (
					<div className="container container--lg">
						<PaymentTimedOutModal handleRoute={handleRoute} />
						<EffectiveWarningSMEContainer
							payType={PaymentType.CHAPS}
							listPayType={PaymentType.LIST_CHAPS}
							batchPayType={PaymentType.BATCH_CHAPS}
							onContinue={this.handleContinue}
							onCancel={this.handleReset}
							onError={() => handleRoute('WEB-HOMEPAGE')}
							handleBack={goToPreviousPage}
							startPage={page}
							handlePaymentInReview={handlePaymentInReview}
							handleRoute={handleRoute}
							content={content}
						/>
					</div>
				);
			default:
				return null;
		}
	}
}

LayoutContainer.propTypes = {
	page: PropTypes.string.isRequired,
	hasError: PropTypes.bool,
	handleRoute: PropTypes.func,
	submitChapsPaymentsForReview: PropTypes.func,
	submitChapsConfirmedPayments: PropTypes.func,
	handleComplete: PropTypes.func,
	handleSelectPaymentType: PropTypes.func,
	isBootStrapInProgress: PropTypes.bool.isRequired,
	handlePaymentInReview: PropTypes.func,
	resetPaymentList: PropTypes.func,
	reset: PropTypes.func,
	fetchCalendar: PropTypes.func,
	handleToggleIsBatch: PropTypes.func.isRequired,
	isBatch: PropTypes.bool.isRequired,
	isBatchEligible: PropTypes.bool.isRequired,
	handleBackParent: PropTypes.func,
	handleDisplayFullwidth: PropTypes.func,
	firstSubmissionDate: PropTypes.instanceOf(moment),
	fetchStatusCodeMapping: PropTypes.func.isRequired,
	isCHAPSTRAObligate: PropTypes.bool.isRequired,
	handleBack: PropTypes.func,
	importedPaymentList: PropTypes.shape([]),
	isBatchTypeUpload: PropTypes.bool.isRequired,
	resetEffectiveWarning: PropTypes.func,
	goToPreviousPage: PropTypes.func,
	copCheckTriggered: PropTypes.func,
	sessionCOPChecks: PropTypes.object,
};

export default connect(mapState, mapDispatch)(withCalendar(LayoutContainer));
